/**
 * Quality / Tests and coverage (design board 5): the totals, the run history (bars: height the duration, colour the
 * result; then a table), the failing tests with their error and Fix with AI / Open in Code / Rerun, the flaky tests with
 * the quarantine switch (`settings.tests.quarantineFlaky`, editors), and the coverage per service (against the gate's
 * minimum) and per file. A service never started since the sandbox woke says so ("Start <svc> once…"), with "Start and
 * test" for editors (it starts the service, waits for its dependencies, then runs its tests), one
 * whose test tool is not supported says that, and a project without tests offers "Write tests with AI".
 * v3 drill-down: every named test of the latest run (QualityTestTable), a run of the history opens in detail
 * (QualityRunDetail), "Coverage analytics" (QualityCoverage), and coverage never measured says so (never 0%), with
 * "Add coverage" (POST /quality/coverage/setup) or the AI.
 */
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import DataTable from '../table/DataTable';
import { Play, Sparkle } from '@phosphor-icons/react';
import { getCheck, getRunDetail, listRuns, setupCoverage } from '../../services/QualityService';
import QualityTestTable from './QualityTestTable';
import { Pill, State, StatusPill, SubHead } from './QualityCommon';
import {
  NO_TESTS_PROMPT, coveragePrompt, coverageSetupPrompt, coverageTone, errorText, fileLine, formatDuration, pct, plural, relativeTime, ruleOn, runLabel, shownStatus, statusOf,
  testPrompt,
} from './qualityModel';

const FRAMEWORKS = { vitest: 'Vitest', jest: 'Jest', 'node-test': 'node:test', mocha: 'Mocha', ava: 'AVA', tap: 'tap', 'npm-test': 'npm test', pytest: 'pytest', unittest: 'unittest', maven: 'Maven', gradle: 'Gradle', go: 'go test', cargo: 'cargo test', dotnet: 'dotnet test' };
const TRIGGERS = { manual: 'Manual', publish: 'Before Publish', change: 'On change' };

function runResult(run) {
  const t = run.testTotals;
  const status = run.statuses?.tests;
  if (run.state === 'interrupted') { return { tone: 'info', text: 'Interrupted' }; }
  if (run.state === 'cancelled') { return { tone: 'info', text: 'Cancelled' }; }
  if (t?.failed) { return { tone: 'fail', text: `${t.failed} failed` }; }
  if (t?.flaky) { return { tone: 'warn', text: `Passed, ${t.flaky} flaky` }; }
  if (status === 'pass' || (t && t.total)) { return { tone: 'pass', text: 'Passed' }; }
  return { tone: statusOf(status).tone, text: status ? statusOf(status).label : 'No tests' };
}

/**
 * Whether a run is one of the tests' runs: it ran the tests check (an "On change" run runs only the checks set to run on
 * change, often not the tests: listed here it said "No tests" next to the manual runs' results), or stopped early.
 */
const ranTests = (run) => Boolean(run.testTotals || (run.statuses && run.statuses.tests) || (run.checksRun || []).includes('tests') || (run.state && run.state !== 'done' && !Object.keys(run.statuses || {}).length));

const runBy = (r) => r.by?.name || (r.trigger === 'change' ? 'After a change' : 'Flow');
function runColumns(onOpenRun) {
  return [
    {
      key: 'run',
      label: 'Run',
      className: 'quality-small',
      value: (r) => r.finishedAt || r.startedAt,
      title: (r) => r.runId,
      render: (r) => (onOpenRun
        ? <button className="quality-link" onClick={() => onOpenRun(r.runId)} title={r.runId} type="button">{runLabel(r)}</button>
        : <span title={r.runId}>{runLabel(r)}</span>),
    },
    { key: 'result', label: 'Result', width: 140, value: (r) => runResult(r).text, render: (r) => { const result = runResult(r); return <Pill tone={result.tone}>{result.text}</Pill>; } },
    { key: 'by', label: 'By', width: 150, value: runBy },
    { key: 'trigger', label: 'Trigger', width: 140, value: (r) => TRIGGERS[r.trigger] || r.trigger },
    { key: 'when', label: 'When', width: 120, value: (r) => r.finishedAt || r.startedAt, render: (r) => relativeTime(r.finishedAt || r.startedAt), title: (r) => new Date(r.finishedAt || r.startedAt).toLocaleString() },
    { key: 'took', label: 'Took', width: 90, value: (r) => r.durationMs, render: (r) => formatDuration(r.durationMs) },
  ];
}

const FLAKY_COLUMNS = [
  { key: 'name', label: 'Test', className: 'quality-mono', value: (f) => f.extra?.name || f.message },
  { key: 'service', label: 'Service', width: 150, value: (f) => f.service || '–' },
  { key: 'message', label: 'Usual failure', width: 260, wrap: 2, className: 'quality-mono quality-small', value: (f) => f.message },
];

function RunHistory({ runs: all, onOpenRun = null }) {
  const runs = all.filter(ranTests);
  const shown = runs.slice(0, 20).reverse();
  const longest = Math.max(1, ...shown.map((r) => r.durationMs || 0));
  const failed = shown.filter((r) => runResult(r).tone === 'fail').length;
  return (
    <section aria-labelledby="quality-runs-title" className="quality-card quality-pad">
      <div className="quality-section-head">
        <h2 id="quality-runs-title">Run history</h2>
        <span className="quality-muted quality-small">Last {shown.length} runs · bar height is duration · <span className="quality-ink-pass">passed</span>, <span className="quality-ink-fail">failed</span></span>
      </div>
      {shown.length > 0 && (
        <div aria-label={`Last ${shown.length} runs: ${shown.length - failed} passed, ${failed} failed${shown.length ? `, the latest ${runResult(shown[shown.length - 1]).tone === 'fail' ? 'failed' : 'passed'}` : ''}`} className="quality-bars" data-testid="quality-run-bars" role="img">
          {shown.map((r) => (
            <span className={`quality-bar is-${runResult(r).tone}`} key={r.runId} style={{ height: `${Math.max(8, Math.round(((r.durationMs || 0) / longest) * 64))}px` }} title={`${runResult(r).text} · ${formatDuration(r.durationMs)}`} />
          ))}
        </div>
      )}
      <div className="quality-table-box">
        <DataTable
          className="quality-table"
          columns={runColumns(onOpenRun)}
          rowKey="runId"
          rowProps={() => ({ 'data-testid': 'quality-run-row' })}
          rows={runs.slice(0, 10)}
          storageKey="flow.quality.runs.hiddenColumns"
        />
      </div>
    </section>
  );
}

RunHistory.propTypes = { onOpenRun: PropTypes.func, runs: PropTypes.array.isRequired };

function ErrorBox({ finding }) {
  const extra = finding.extra || {};
  const stack = extra.stack ? String(extra.stack).split('\n').filter((l) => l.trim() && l.trim() !== finding.message).slice(0, 8) : [];
  return (
    <pre className="quality-err">
      <span className="is-bad">{finding.message}</span>
      {extra.expected !== undefined && <>{'\n'}<span className="is-good">- Expected  {String(extra.expected)}</span></>}
      {extra.received !== undefined && <>{'\n'}<span className="is-bad">+ Received  {String(extra.received)}</span></>}
      {stack.map((line, i) => <React.Fragment key={i}>{'\n'}<span className="is-dim">{line}</span></React.Fragment>)}
    </pre>
  );
}

ErrorBox.propTypes = { finding: PropTypes.object.isRequired };

export default function QualityTests({
  projectId, overview, canEdit = false, onAskAI = null, onBack, onRun, onOpenCode = null, onSaveSettings = null, onStartAndTest = null,
  onOpenRun = null, onOpenCoverage = null, onOpenCoverageFile = null, runNote = null,
}) {
  const [check, setCheck] = useState(null);
  const [runs, setRuns] = useState([]);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [latest, setLatest] = useState(null);
  const [setup, setSetup] = useState(null);
  const runId = overview?.run?.runId || null;
  const running = Boolean(overview?.running);
  const starting = overview?.starting?.state === 'starting' ? overview.starting : null;
  const startingOf = (service) => Boolean(starting?.services?.some((s) => s.service === service && s.state !== 'ready'));
  const busy = running || Boolean(starting);
  const card = overview?.checks?.tests || null;
  const settings = overview?.settings || {};
  const min = settings.gate?.coverage?.min ?? 70;
  const coverageRule = ruleOn(settings.gate?.coverage);

  const load = useCallback(async () => {
    try {
      const [answer, history] = await Promise.all([
        getCheck(projectId, 'tests', { includeIgnored: true }),
        listRuns(projectId).catch(() => ({ runs: [] })),
      ]);
      setCheck(answer);
      setRuns(history?.runs || []);
      setError(null);
    } catch (e) {
      setError(e);
      listRuns(projectId).then((h) => setRuns(h?.runs || [])).catch(() => {});
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load, runId]);
  // Every named test of the newest run with test detail (an older sandbox: none, the page stays as it was).
  useEffect(() => {
    let live = true;
    Promise.resolve().then(() => getRunDetail(projectId, 'latest')).then((detail) => live && setLatest(detail)).catch(() => live && setLatest(null));
    return () => { live = false; };
  }, [projectId, runId]);

  const runAll = () => onRun({ checks: ['tests'] });
  const runFailed = () => onRun({ checks: ['tests'], failedOnly: true });
  const ask = (prompt) => onAskAI?.(prompt);

  const head = (
    <SubHead onBack={onBack} title="Tests and coverage">
      {card && <StatusPill status={running && overview.running.checks?.tests !== 'done' ? 'running' : shownStatus(card)} />}
      {card?.blocks && <Pill tone="fail">Blocks Publish</Pill>}
      <span className="quality-grow" />
      {canEdit && check?.details?.totals?.failed > 0 && <button className="quality-btn" disabled={running} onClick={runFailed} type="button">Run failed only</button>}
      {canEdit && <button className="quality-btn is-primary" disabled={running} onClick={runAll} type="button"><Play aria-hidden size={13} weight="fill" />Run all tests</button>}
    </SubHead>
  );

  if (error && !check) {
    const notRun = error.code === 'not_run' || (error.status === 404 && error.code !== 'sandbox_outdated');
    return (
      <div className="quality-sub" data-testid="quality-tests">
        {head}
      {runNote}
        {runNote}
        {notRun
          ? <State title="The tests have not run yet">{canEdit ? 'Run all tests to see the results and the coverage here.' : 'An editor can run them.'}</State>
          : <State action={<button className="quality-btn" onClick={load} type="button">Try again</button>} title="Could not load the tests" tone="bad">{errorText(error)}</State>}
      </div>
    );
  }
  if (!check) {
    return <div className="quality-sub" data-testid="quality-tests">{head}{runNote}<div className="quality-state" role="status">Loading the tests…</div></div>;
  }

  const details = check.details || {};
  const totals = details.totals || { total: 0, failed: 0, flaky: 0 };
  const services = details.services || [];
  const findings = check.findings || [];
  const failures = findings.filter((f) => f.rule === 'test-failed');
  const flaky = findings.filter((f) => f.rule === 'test-flaky');
  const notes = services.filter((s) => ['not_ready', 'unsupported', 'error', 'skipped'].includes(s.status));
  const frameworks = [...new Set(services.map((s) => FRAMEWORKS[s.framework]).filter(Boolean))];
  const withCoverage = services.filter((s) => s.coverage && s.coverage.lines !== null && s.coverage.lines !== undefined);
  const files = services.flatMap((s) => (s.files || []).map((f) => ({ ...f, service: s.service }))).sort((a, b) => (a.lines ?? 0) - (b.lines ?? 0));
  const quarantine = Boolean(settings.tests?.quarantineFlaky);
  const noTests = !totals.total && !notes.length;
  const lastRun = runs.find(ranTests);
  // Coverage not measured (no coverage tool, or nothing of the code loaded): never shown as 0%.
  const notMeasured = details.coverage?.notMeasured
    || services.filter((s) => (s.total || 0) > 0 && !s.coverage && !['not_ready', 'unsupported', 'error', 'skipped'].includes(s.status)).map((s) => s.service);
  const coverageMeasured = details.coverage?.measured !== false && (details.coverage?.lines !== null && details.coverage?.lines !== undefined);
  const coverageNote = services.map((s) => s.coverageNote).filter(Boolean)[0] || details.coverageNote || null;
  const addCoverage = async () => {
    setSetup({ busy: true, results: [] });
    try {
      const answer = await setupCoverage(projectId, notMeasured);
      setSetup({ busy: false, results: answer.results || [] });
    } catch (e) {
      setSetup({ busy: false, results: [], error: errorText(e) });
    }
  };
  const latestServices = latest?.tests?.services || [];

  const toggleQuarantine = async (event) => {
    if (!onSaveSettings) { return; }
    setSaving(true);
    try {
      await onSaveSettings({ ...settings, tests: { ...(settings.tests || {}), quarantineFlaky: event.target.checked } });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="quality-sub" data-testid="quality-tests">
      {head}

      {notes.length > 0 && (
        <ul aria-label="Services not tested" className="quality-notes">
          {notes.map((s) => (
            <li data-testid={`quality-tests-note-${s.service}`} key={s.service}>
              <span className="quality-mono">{s.service}</span>
              <StatusPill status={s.status} />
              <span>{s.message || (s.status === 'unsupported' ? 'Its test tool is not supported yet.' : statusOf(s.status).label)}</span>
              {s.status === 'not_ready' && canEdit && onStartAndTest && (
                <button className="quality-btn is-sm" data-testid={`quality-start-and-test-${s.service}`} disabled={busy} onClick={() => onStartAndTest([s.service])} type="button">
                  <Play aria-hidden size={12} weight="fill" />{startingOf(s.service) ? 'Starting…' : 'Start and test'}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {noTests ? (
        <State
          action={canEdit && onAskAI ? <button className="quality-btn is-primary" onClick={() => ask(NO_TESTS_PROMPT)} type="button"><Sparkle aria-hidden size={13} />Write tests with AI</button> : null}
          testId="quality-no-tests"
          title="No tests yet"
        >
          No service of this project has tests Flow can run. Vitest, Jest and npm test for Node, pytest for Python, Maven and Gradle for Java, go test for Go, dotnet test for .NET.
        </State>
      ) : (
        <section aria-label="Totals" className="quality-card quality-stats" data-testid="quality-tests-stats">
          <div className="quality-stat"><b>{totals.total}</b><span>{totals.total === 1 ? 'test' : 'tests'}{frameworks.length ? ` · ${frameworks.join(', ')}` : ''}</span></div>
          <div className="quality-stat"><b className={totals.failed ? 'quality-ink-fail' : ''}>{totals.failed} failed</b><span>{totals.skipped ? `${totals.skipped} skipped` : `${totals.passed ?? totals.total - totals.failed} passed`}</span></div>
          <div className="quality-stat"><b className={totals.flaky ? 'quality-ink-warn' : ''}>{totals.flaky || 0} flaky</b><span>quarantine is {quarantine ? 'on' : 'off'}</span></div>
          <div className="quality-stat"><b>{coverageMeasured ? pct(details.coverage?.lines) : 'Not measured'}</b><span>line coverage{coverageRule ? ` · rule ${min}%` : ''}</span></div>
          <div className="quality-stat"><b>{formatDuration(check.durationMs)}</b><span>{lastRun ? `last run ${relativeTime(lastRun.finishedAt || lastRun.startedAt)}` : 'last run'}</span></div>
        </section>
      )}

      {runs.some(ranTests) && <RunHistory onOpenRun={onOpenRun} runs={runs} />}

      {latestServices.some((s) => (s.tests || []).length) && (
        <section aria-labelledby="quality-all-tests-title" className="quality-card quality-pad" data-testid="quality-all-tests">
          <div className="quality-section-head">
            <h2 id="quality-all-tests-title">Every test</h2>
            {latest.run && <span className="quality-muted quality-small" title={latest.run.runId}>Run {runLabel(latest.run)}</span>}
          </div>
          <QualityTestTable canEdit={canEdit} onAskAI={onAskAI} onOpenCode={onOpenCode} services={latestServices} truncated={Boolean(latest.tests?.truncated)} />
        </section>
      )}

      {failures.length > 0 && (
        <section aria-labelledby="quality-failing-title" className="quality-stack">
          <h2 id="quality-failing-title">Failing tests <Pill tone="fail">{failures.length}</Pill></h2>
          {failures.map((f) => (
            <article className="quality-card quality-pad quality-failure" data-testid={`quality-failure-${f.id}`} key={f.id}>
              <div className="quality-failure-head">
                <h3 className="quality-mono">{[f.extra?.suite, f.extra?.name].filter(Boolean).join(' › ') || f.message}</h3>
                {f.service && <Pill tone="info">{[f.service, f.extra?.kind].filter(Boolean).join(' · ')}</Pill>}
                {f.ignored && <Pill title={`${f.ignored.reason} (${f.ignored.by})`} tone="info">Ignored</Pill>}
              </div>
              {f.file && (onOpenCode
                ? <button className="quality-link quality-mono quality-small" onClick={() => onOpenCode({ path: f.file, line: f.line })} type="button">{fileLine(f)}</button>
                : <span className="quality-mono quality-small">{fileLine(f)}</span>)}
              <ErrorBox finding={f} />
              <div className="quality-actions">
                {canEdit && onAskAI && <button className="quality-btn is-sm is-primary" onClick={() => ask(testPrompt(f))} type="button"><Sparkle aria-hidden size={13} />Fix with AI</button>}
                {f.file && onOpenCode && <button className="quality-btn is-sm" onClick={() => onOpenCode({ path: f.file, line: f.line })} type="button">Open in Code</button>}
                {canEdit && <button className="quality-btn is-sm" disabled={running} onClick={() => onRun({ checks: ['tests'], services: f.service ? [f.service] : undefined, failedOnly: true })} type="button">Rerun</button>}
              </div>
            </article>
          ))}
        </section>
      )}

      {(flaky.length > 0 || canEdit) && !noTests && (
        <section aria-labelledby="quality-flaky-title" className="quality-card quality-pad">
          <div className="quality-section-head">
            <h2 id="quality-flaky-title">Flaky tests</h2>
            <label className="quality-check-label">
              <input checked={quarantine} disabled={!canEdit || !onSaveSettings || saving} onChange={toggleQuarantine} type="checkbox" />
              Quarantine flaky tests (they run, but do not block Publish)
            </label>
          </div>
          {flaky.length ? (
            <div className="quality-table-box">
              <DataTable
                actions={{
                  label: 'Actions',
                  width: 120,
                  render: (f) => (canEdit && onAskAI ? <button className="quality-btn is-sm" onClick={() => ask(testPrompt(f).replace('A test of this project fails.', 'A test of this project is flaky: it sometimes fails, then passes.'))} type="button">Fix with AI</button> : null),
                }}
                className="quality-table"
                columns={FLAKY_COLUMNS}
                rowKey="id"
                rowProps={(f) => ({ 'data-testid': `quality-flaky-${f.id}` })}
                rows={flaky}
              />
            </div>
          ) : <p className="quality-muted quality-small">No flaky test in the last run: a failing test that passes when run again shows here.</p>}
        </section>
      )}

      {!noTests && (
        <section aria-labelledby="quality-coverage-title" className="quality-card quality-pad" data-testid="quality-coverage">
          <div className="quality-section-head">
            <h2 id="quality-coverage-title">Coverage</h2>
            <span className="quality-actions">
              {onOpenCoverage && <button className="quality-btn" onClick={onOpenCoverage} type="button">Coverage analytics</button>}
              {canEdit && onAskAI && files.length > 0 && (
                <button className="quality-btn" onClick={() => ask(coveragePrompt(files, min))} type="button"><Sparkle aria-hidden size={13} />Write tests with AI for the {Math.min(5, files.length)} least covered files</button>
              )}
            </span>
          </div>
          {notMeasured.length > 0 && (
            <div className="quality-todo is-info" data-testid="quality-cov-not-measured">
              <span className="quality-todo-text">
                <strong>Coverage not measured</strong> for {notMeasured.join(', ')}. {coverageNote || 'The tests ran without a coverage report.'}
              </span>
              {canEdit && <button className="quality-btn is-sm is-primary" disabled={Boolean(setup?.busy)} onClick={addCoverage} type="button">{setup?.busy ? 'Adding coverage…' : 'Add coverage'}</button>}
              {canEdit && onAskAI && <button className="quality-btn is-sm" onClick={() => ask(coverageSetupPrompt(notMeasured))} type="button"><Sparkle aria-hidden size={13} />Ask AI to add coverage</button>}
            </div>
          )}
          {setup && !setup.busy && (
            <ul aria-label="Add coverage" className="quality-notes" data-testid="quality-cov-setup">
              {setup.error && <li>Could not add coverage: {setup.error}</li>}
              {setup.results.map((r) => (
                <li key={r.service}>
                  <span>{r.service}: {r.message}</span>
                  <Pill className="is-xs" tone={r.status === 'added' || r.status === 'exists' ? 'pass' : 'warn'}>{r.status}</Pill>
                </li>
              ))}
              {canEdit && setup.results.some((r) => r.status === 'added') && <li><button className="quality-btn is-sm" disabled={running} onClick={runAll} type="button">Run the tests with coverage</button></li>}
            </ul>
          )}
          {withCoverage.length ? (
            <div className="quality-cov-grid">
              {withCoverage.map((s) => {
                const tone = coverageTone(s.coverage.lines, min);
                return (
                  <div className="quality-cov" data-testid={`quality-cov-${s.service}`} key={s.service}>
                    <div className="quality-cov-head"><span className="quality-mono">{s.service}</span><b>{pct(s.coverage.lines)}</b></div>
                    <div aria-label={`${s.service}: ${pct(s.coverage.lines)} of lines covered${coverageRule ? `, the rule asks for ${min}%` : ''}`} className="quality-meter" role="img">
                      <span className={`quality-meter-fill is-${tone}`} style={{ width: `${Math.max(0, Math.min(100, s.coverage.lines))}%` }} />
                      {coverageRule && <span aria-hidden className="quality-meter-mark" style={{ left: `${min}%` }} />}
                    </div>
                    <span className="quality-muted quality-small">{[FRAMEWORKS[s.framework], plural(s.total || 0, 'test'), s.coverage.branches !== undefined && s.coverage.branches !== null ? `branches ${pct(s.coverage.branches)}` : null].filter(Boolean).join(' · ')}</span>
                  </div>
                );
              })}
            </div>
          ) : (!notMeasured.length && (
            <p className="quality-muted quality-small">
              {coverageNote || 'No coverage measured: add @vitest/coverage-v8 (Vitest), use Jest\'s coverage, or pytest-cov (Python).'}
            </p>
          ))}
          {files.length > 0 && (
            <div className="quality-table-box">
              <DataTable
                className="quality-table"
                columns={[
                  {
                    key: 'path',
                    label: 'File',
                    className: 'quality-mono quality-small',
                    value: (f) => f.path,
                    title: (f) => f.path,
                    render: (f) => (onOpenCoverageFile || onOpenCode
                      ? <button className="quality-link quality-mono" onClick={() => (onOpenCoverageFile ? onOpenCoverageFile(f.path) : onOpenCode({ path: f.path }))} type="button">{f.path}</button>
                      : f.path),
                  },
                  {
                    key: 'lines',
                    label: 'Lines',
                    width: 150,
                    value: (f) => f.lines,
                    render: (f) => (
                      <span className="quality-inline-meter">
                        <span className="quality-meter is-small"><span className={`quality-meter-fill is-${coverageTone(f.lines, min)}`} style={{ width: `${Math.max(0, Math.min(100, f.lines || 0))}%` }} /></span>
                        {pct(f.lines)}
                      </span>
                    ),
                  },
                  { key: 'branches', label: 'Branches', width: 100, value: (f) => f.branches, render: (f) => pct(f.branches) },
                  { key: 'uncovered', label: 'Lines not covered', width: 200, className: 'quality-mono quality-small', value: (f) => f.uncovered || '–' },
                ]}
                rowKey={(f) => `${f.service}:${f.path}`}
                rows={files.slice(0, 50)}
                storageKey="flow.quality.testFiles.hiddenColumns"
              />
            </div>
          )}
        </section>
      )}
    </div>
  );
}

QualityTests.propTypes = {
  canEdit: PropTypes.bool,
  onAskAI: PropTypes.func,
  onBack: PropTypes.func.isRequired,
  // ({ path, line }) => void: the file in Code.
  onOpenCode: PropTypes.func,
  // ({ checks, services?, failedOnly? }) => void
  onRun: PropTypes.func.isRequired,
  // Why the tests did not start when Run (or Start and test) was pressed here (QualityPanel RunNote), or null.
  runNote: PropTypes.node,
  // (settings) => Promise: saves the whole settings (the quarantine switch).
  onSaveSettings: PropTypes.func,
  // () => void: Coverage analytics.
  onOpenCoverage: PropTypes.func,
  // (path) => void: a file's coverage (its lines).
  onOpenCoverageFile: PropTypes.func,
  // (runId) => void: one run in detail.
  onOpenRun: PropTypes.func,
  // (services) => void: "Start and test" for services never started since the sandbox woke.
  onStartAndTest: PropTypes.func,
  overview: PropTypes.object,
  projectId: PropTypes.string.isRequired,
};
